import type { ReactNode } from 'react';

import useSolutionResourceDownload from '@/hooks/apps/useSolutionResourceDownload';

import type { SolutionResource } from '@/data/shared/solutionResources';

type Props = {
  children: ReactNode;
  className: string;
  productName?: string;
  resource: SolutionResource;
};

/**
 * ### ResourceSectionItem
 *
 * One link of the solution components resource list. It exists as its own
 * component because resolving a resource is a hook, and the list renders one
 * resource per item.
 *
 * The anchor is a plain element rather than the locale-aware link component:
 * the download path carries the language in its own segment already, and a
 * locale prefix on top of it would not resolve.
 *
 * @param props.children Trailing icon rendered at the end of the row
 * @param props.className Layout classes owned by the section
 * @param props.productName Product to print above the title, when the section shows one
 * @param props.resource Resource the link offers
 */
export default function ResourceSectionItem({
  children,
  className,
  productName,
  resource,
}: Props) {
  const { href, onClick, title } = useSolutionResourceDownload(resource);

  return (
    <a
      className={className}
      href={href}
      onClick={onClick}
      rel="noopener"
      target="_blank"
    >
      <div className="flex flex-col gap-[4px] group-hover:text-apps-corePrimary01">
        {productName && (
          <div className="text-[14px] font-semibold leading-[140%] tracking-[-0.28px] text-apps-brandPrimary500 lg:text-[16px] lg:tracking-[-0.32px]">
            {productName}
          </div>
        )}
        <div className="text-[16px] font-bold leading-[140%] tracking-[-0.32px] transition-[color] duration-300 ease-[ease] lg:text-[clamp(16px,1.833vw,22px)] lg:leading-[160%] lg:tracking-[-0.44px]">
          {title}
        </div>
      </div>
      {children}
    </a>
  );
}
